'use client';

import * as React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { JIcon } from '../../icons';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';
import { devWarn, IS_PROD } from '../internal/devWarn';
import { Popover } from './Popover';

export interface MultiSelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  swatch?: string;
  disabled?: boolean;
}

export interface MultiSelectAction {
  label: string;
  onSelect: () => void;
}

export interface MultiSelectFieldProps extends UnsafeProps {
  label?: ReactNode;
  value: string[];
  options: MultiSelectOption[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  maxInlineItems?: number;
  renderOption?: (option: MultiSelectOption, selected: boolean) => ReactNode;
  /** @deprecated Selected tray tokens were replaced by a fixed count row; use renderOption for option content. */
  renderSelectedToken?: (option: MultiSelectOption, remove: () => void) => ReactNode;
  action?: MultiSelectAction;
  clearLabel?: string;
  doneLabel?: string;
  search?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

let didWarnRenderSelectedTokenDeprecated = false;

function summarizeSelection(
  selected: MultiSelectOption[],
  placeholder: string | undefined,
  maxInlineItems: number,
): string {
  if (selected.length === 0) return placeholder ?? '';
  if (selected.length <= maxInlineItems) return selected.map((o) => o.label).join(', ');
  if (maxInlineItems <= 0) return `${selected.length} selected`;
  const head = selected
    .slice(0, maxInlineItems)
    .map((o) => o.label)
    .join(', ');
  return `${head} +${selected.length - maxInlineItems}`;
}

export const MultiSelectField = React.forwardRef<HTMLButtonElement, MultiSelectFieldProps>(
  function MultiSelectField(
    {
      label,
      value,
      options,
      onChange,
      placeholder,
      maxInlineItems = 2,
      renderOption,
      renderSelectedToken,
      action,
      clearLabel = 'Clear all',
      doneLabel = 'Done',
      search = false,
      searchPlaceholder = 'Search...',
      emptyMessage = 'No results',
      name,
      size = 'md',
      disabled,
      className,
      style,
      unsafe_className,
      unsafe_style,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
    },
    ref,
  ) {
    const [open, setOpen] = React.useState(false);
    const [focusedIndex, setFocusedIndex] = React.useState(0);
    const [showFocusedOption, setShowFocusedOption] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [searchFocused, setSearchFocused] = React.useState(false);
    const [triggerWidth, setTriggerWidth] = React.useState<number | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const searchInputRef = React.useRef<HTMLInputElement | null>(null);
    const listboxRef = React.useRef<HTMLDivElement | null>(null);
    const trayRef = React.useRef<HTMLDivElement | null>(null);
    const pendingListboxScrollTopRef = React.useRef<number | null>(null);
    const searchFocusedRef = React.useRef(false);
    const restoreSearchFocusAfterPointerRef = React.useRef(false);
    const listboxId = React.useId();
    const labelId = React.useId();

    const selectedSet = React.useMemo(() => new Set(value), [value]);
    const selectedOptions = React.useMemo(
      () =>
        value.map((v) => options.find((o) => o.value === v)).filter(Boolean) as MultiSelectOption[],
      [options, value],
    );
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const visibleOptions = React.useMemo(
      () =>
        options
          .map((option, index) => ({ option, index }))
          .filter(
            ({ option }) =>
              !normalizedSearch || option.label.toLowerCase().includes(normalizedSearch),
          ),
      [normalizedSearch, options],
    );
    const visibleIndices = React.useMemo(
      () => visibleOptions.map(({ index }) => index),
      [visibleOptions],
    );
    const focusableIndices = React.useMemo(
      () => (action ? [...visibleIndices, options.length] : visibleIndices),
      [action, options.length, visibleIndices],
    );
    const actionIndex = options.length;
    const maxFocusableIndex = action ? actionIndex : options.length - 1;
    const summary = summarizeSelection(selectedOptions, placeholder, maxInlineItems);
    const hasSelection = selectedOptions.length > 0;

    React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

    React.useEffect(() => {
      if (IS_PROD || !renderSelectedToken || didWarnRenderSelectedTokenDeprecated) return;
      didWarnRenderSelectedTokenDeprecated = true;
      devWarn(
        '[JKit.MultiSelectField] `renderSelectedToken` is deprecated and no longer renders ' +
          'popover tray chips. MultiSelectField now uses a fixed selected-count row so long ' +
          'selection sets cannot crowd out the option list. Use `renderOption` for custom ' +
          'option content.',
      );
    }, [renderSelectedToken]);

    const close = React.useCallback((returnFocus = true) => {
      setOpen(false);
      setShowFocusedOption(false);
      setSearchQuery('');
      setSearchFocused(false);
      if (returnFocus && triggerRef.current?.isConnected) triggerRef.current.focus();
    }, []);

    const openList = React.useCallback(
      (showVisualFocus = false) => {
        if (disabled) return;
        setTriggerWidth(triggerRef.current?.offsetWidth ?? null);
        setOpen(true);
        setShowFocusedOption(showVisualFocus);
        const firstSelected = options.findIndex((o) => selectedSet.has(o.value) && !o.disabled);
        const firstEnabled = options.findIndex((o) => !o.disabled);
        setFocusedIndex(firstSelected >= 0 ? firstSelected : Math.max(firstEnabled, 0));
      },
      [disabled, options, selectedSet],
    );

    const toggleValue = React.useCallback(
      (option: MultiSelectOption) => {
        if (disabled || option.disabled) return;
        pendingListboxScrollTopRef.current = listboxRef.current?.scrollTop ?? null;
        const next = selectedSet.has(option.value)
          ? value.filter((v) => v !== option.value)
          : [...value, option.value];
        onChange(next);
      },
      [disabled, onChange, selectedSet, value],
    );

    const toggleValueFromPointer = React.useCallback(
      (option: MultiSelectOption) => {
        if (disabled || option.disabled) return;
        toggleValue(option);
        if (restoreSearchFocusAfterPointerRef.current) {
          restoreSearchFocusAfterPointerRef.current = false;
          if (searchInputRef.current?.isConnected) searchInputRef.current.focus();
        }
      },
      [disabled, toggleValue],
    );

    const preserveFocusFromPointer = React.useCallback((event: React.PointerEvent) => {
      restoreSearchFocusAfterPointerRef.current = searchFocusedRef.current;
      if (event.pointerType === 'mouse') event.preventDefault();
    }, []);

    const preserveFocusFromMouse = React.useCallback((event: React.MouseEvent) => {
      restoreSearchFocusAfterPointerRef.current = searchFocusedRef.current;
      event.preventDefault();
    }, []);

    const moveFocus = React.useCallback(
      (direction: 1 | -1) => {
        if (focusableIndices.length === 0) return;
        setShowFocusedOption(true);
        const currentPosition = focusableIndices.indexOf(focusedIndex);
        let position = currentPosition === -1 ? 0 : currentPosition;
        for (let i = 0; i < focusableIndices.length; i++) {
          position = (position + direction + focusableIndices.length) % focusableIndices.length;
          const next = focusableIndices[position] ?? focusableIndices[0]!;
          if (next === actionIndex && action) {
            setFocusedIndex(next);
            return;
          }
          if (!options[next]?.disabled) {
            setFocusedIndex(next);
            return;
          }
        }
      },
      [action, actionIndex, focusableIndices, focusedIndex, options],
    );

    React.useEffect(() => {
      if (!open || !search) return;
      if (focusableIndices.length === 0) {
        setFocusedIndex(-1);
        return;
      }
      if (!focusableIndices.includes(focusedIndex)) {
        setFocusedIndex(focusableIndices[0] ?? -1);
      }
    }, [focusableIndices, focusedIndex, open, search]);

    React.useEffect(() => {
      if (!open || !showFocusedOption || focusedIndex < 0 || !listboxRef.current) return;
      if (pendingListboxScrollTopRef.current != null) return;
      const optionEl = listboxRef.current.querySelector<HTMLElement>(
        `[data-multiselect-index="${focusedIndex}"]`,
      );
      if (typeof optionEl?.scrollIntoView === 'function') {
        optionEl.scrollIntoView({ block: 'nearest' });
      }
    }, [focusedIndex, open, showFocusedOption]);

    React.useLayoutEffect(() => {
      const pendingScrollTop = pendingListboxScrollTopRef.current;
      if (pendingScrollTop == null || !listboxRef.current) return;
      listboxRef.current.scrollTop = pendingScrollTop;
      pendingListboxScrollTopRef.current = null;
    }, [value]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            if (!open) openList(true);
            else moveFocus(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            if (!open) openList(true);
            else moveFocus(-1);
            break;
          case 'Home':
            if (!open) break;
            event.preventDefault();
            setShowFocusedOption(true);
            setFocusedIndex(
              Math.max(visibleOptions.find(({ option }) => !option.disabled)?.index ?? -1, 0),
            );
            break;
          case 'End':
            if (!open) break;
            event.preventDefault();
            setShowFocusedOption(true);
            if (action) setFocusedIndex(actionIndex);
            else {
              setFocusedIndex(
                Math.max(
                  visibleOptions.reduce(
                    (last, { option, index }) => (option.disabled ? last : index),
                    -1,
                  ),
                  0,
                ),
              );
            }
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (!open) {
              openList(true);
            } else {
              if (focusedIndex === actionIndex && action) action.onSelect();
              else {
                const focused = options[focusedIndex];
                if (focused) toggleValue(focused);
              }
            }
            break;
          case 'Escape':
            if (!open) break;
            event.preventDefault();
            close(true);
            break;
          case 'Tab':
            if (open && hasSelection && !event.shiftKey) {
              const firstTrayControl = trayRef.current?.querySelector<HTMLElement>('button');
              if (firstTrayControl) {
                event.preventDefault();
                firstTrayControl.focus();
              }
            }
            break;
        }
      },
      [
        action,
        actionIndex,
        close,
        disabled,
        focusedIndex,
        hasSelection,
        moveFocus,
        open,
        openList,
        options,
        toggleValue,
        visibleOptions,
      ],
    );

    const handleSearchKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setShowFocusedOption(true);
            moveFocus(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            setShowFocusedOption(true);
            moveFocus(-1);
            break;
          case 'Enter':
            event.preventDefault();
            if (focusedIndex === actionIndex && action) action.onSelect();
            else {
              const focused = options[focusedIndex];
              if (focused) toggleValue(focused);
            }
            break;
          case 'Escape':
            event.preventDefault();
            close(true);
            break;
          case 'Tab':
            if (hasSelection && !event.shiftKey) {
              const firstTrayControl = trayRef.current?.querySelector<HTMLElement>('button');
              if (firstTrayControl) {
                event.preventDefault();
                firstTrayControl.focus();
              }
            } else {
              close(false);
            }
            break;
        }
      },
      [
        action,
        actionIndex,
        close,
        disabled,
        focusedIndex,
        hasSelection,
        moveFocus,
        options,
        toggleValue,
      ],
    );

    const mergedStyle = unsafe_style ? { ...style, ...unsafe_style } : style;
    const resolvedAriaLabelledBy = ariaLabelledBy ?? (label ? labelId : undefined);
    const activeDescendant =
      open && focusedIndex >= 0 && focusedIndex <= maxFocusableIndex
        ? `${listboxId}-opt-${focusedIndex}`
        : undefined;

    return (
      <div className={cx('j-multiselect-field', className)} style={mergedStyle}>
        {label && (
          <span id={labelId} className="j-multiselect-label">
            {label}
          </span>
        )}
        <div className="j-multiselect-wrapper">
          <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={open ? listboxId : undefined}
            aria-activedescendant={!searchFocused ? activeDescendant : undefined}
            aria-label={ariaLabel}
            aria-labelledby={resolvedAriaLabelledBy}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            className={cx(
              'j-multiselect-trigger',
              size === 'sm' && 'j-multiselect-sm',
              size === 'lg' && 'j-multiselect-lg',
              !hasSelection && 'j-multiselect-empty',
              unsafe_className,
            )}
            onClick={() => {
              if (disabled) return;
              if (open) close(true);
              else openList();
            }}
            onKeyDown={handleKeyDown}
          >
            <span className="j-multiselect-value">{summary}</span>
          </button>

          {name &&
            value.map((v) => (
              <input key={v} type="hidden" name={name} value={v} disabled={disabled} />
            ))}

          <Popover
            open={open}
            onClose={() => close(false)}
            anchorRef={triggerRef}
            placement="bottom"
            align="start"
            panelRole={null}
            mobilePresentation="sheet"
            className="j-menu j-multiselect-popover"
            // eslint-disable-next-line @atelierpx/jacquard/no-experimental-without-prefix -- internal Popover sizing handoff; MultiSelectField owns this measured min-width.
            unsafe_style={triggerWidth ? { minWidth: Math.max(triggerWidth, 360) } : undefined}
          >
            {search && (
              <label className="j-select-search j-multiselect-search">
                <JIcon intent="search" decorative size={14} strokeWidth={2} />
                <input
                  ref={searchInputRef}
                  type="text"
                  role="searchbox"
                  value={searchQuery}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  aria-controls={listboxId}
                  aria-activedescendant={searchFocused ? activeDescendant : undefined}
                  autoComplete="off"
                  onFocus={() => {
                    searchFocusedRef.current = true;
                    setSearchFocused(true);
                  }}
                  onBlur={() => {
                    searchFocusedRef.current = false;
                    setSearchFocused(false);
                  }}
                  onChange={(event) => setSearchQuery(event.currentTarget.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </label>
            )}
            {hasSelection && (
              <div
                ref={trayRef}
                className="j-multiselect-selected-tray"
                aria-label="Selected options"
              >
                <div className="j-multiselect-selected-head">
                  <span className="j-multiselect-selected-count">
                    Selected ({selectedOptions.length})
                  </span>
                  <button
                    type="button"
                    className="j-multiselect-clear"
                    onClick={() => onChange([])}
                  >
                    {clearLabel}
                  </button>
                </div>
              </div>
            )}
            {search && visibleOptions.length === 0 && (
              <div className="j-select-empty" role="status">
                {emptyMessage}
              </div>
            )}
            <div
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              aria-label={ariaLabel}
              aria-labelledby={resolvedAriaLabelledBy}
              className="j-multiselect-listbox"
            >
              {visibleOptions.map(({ option, index }) => {
                const selected = selectedSet.has(option.value);
                return (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- keyboard interaction is handled on the combobox trigger via aria-activedescendant.
                  <div
                    key={option.value}
                    id={`${listboxId}-opt-${index}`}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={option.disabled || undefined}
                    data-multiselect-index={index}
                    className={cx(
                      'j-menu-item',
                      'j-multiselect-option',
                      selected && 'j-multiselect-option-selected',
                      showFocusedOption && index === focusedIndex && 'j-select-option-focused',
                      option.disabled && 'j-multiselect-option-disabled',
                    )}
                    onPointerDown={preserveFocusFromPointer}
                    onMouseDown={preserveFocusFromMouse}
                    onClick={() => toggleValueFromPointer(option)}
                    onMouseEnter={() => {
                      if (!option.disabled) {
                        setShowFocusedOption(true);
                        setFocusedIndex(index);
                      }
                    }}
                  >
                    <span className="j-multiselect-option-check" aria-hidden="true">
                      {selected && <JIcon intent="check" decorative size={12} strokeWidth={2.5} />}
                    </span>
                    {option.swatch && (
                      <span
                        className="j-multiselect-swatch"
                        style={{ background: option.swatch }}
                        aria-hidden="true"
                      />
                    )}
                    {option.icon && (
                      <span className="j-select-option-icon" aria-hidden="true">
                        {option.icon}
                      </span>
                    )}
                    <span className="j-select-option-label">
                      {renderOption ? renderOption(option, selected) : option.label}
                    </span>
                  </div>
                );
              })}
              {action && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- keyboard interaction is handled on the combobox trigger; this is a pointer action row in the listbox popup.
                <div
                  id={`${listboxId}-opt-${actionIndex}`}
                  role="option"
                  aria-selected="false"
                  data-multiselect-index={actionIndex}
                  className="j-menu-item j-multiselect-option j-select-option-action"
                  onClick={action.onSelect}
                  onMouseEnter={() => {
                    setShowFocusedOption(true);
                    setFocusedIndex(actionIndex);
                  }}
                >
                  <span className="j-multiselect-option-check" aria-hidden="true">
                    <JIcon intent="plus" decorative size={12} strokeWidth={2.5} />
                  </span>
                  <span className="j-select-option-label">{action.label}</span>
                </div>
              )}
            </div>
            <div className="j-multiselect-mobile-actions">
              <button type="button" className="j-multiselect-done" onClick={() => close(true)}>
                {doneLabel}
              </button>
            </div>
          </Popover>
        </div>
      </div>
    );
  },
);
